import { Circle, Line, Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useRef, useMemo, useState, useEffect } from 'react';
import { Vector3, Object3D, MathUtils } from 'three';

interface ArchHeightAnnotationProps {
	archHeight: string;
	visible: boolean;
	isLeft: boolean;
	archHeightValue?: number; // Numeric value for animation (0-1 range)
	startDelay?: number; // Delay before starting the animation sequence (in seconds)
}

// Animation timing constants (in seconds)
const MIDDLE_CIRCLE_FADE_DURATION = 0.2; // Duration for middle circle to fade in
const LINES_START_DELAY = 0.3; // Start animating lines after middle circle appears
const LINES_ANIMATION_DURATION = 0.6; // Duration for lines to animate
const TOP_CIRCLE_DELAY = LINES_START_DELAY + LINES_ANIMATION_DURATION; // Show when vertical line finishes
const TEXT_FADE_DELAY = LINES_START_DELAY + LINES_ANIMATION_DURATION; // Show when bottom line finishes
const TEXT_FADE_DURATION = 0.4; // Duration for text fade in

// Arch height positioning constants
const BASE_TOP_Y = 0.38; // Base Y position for the top indicator when arch is neutral
const ARCH_HEIGHT_SCALE = 0.5; // Scale factor to match the shader deformation (tune this value)

// Single annotation for one insole - will be placed inside the insole's group
export const ArchHeightAnnotation = ({ archHeight, visible, isLeft, archHeightValue = 0.07142857142, startDelay = 1 }: ArchHeightAnnotationProps) => {
	const { invalidate } = useThree();
	const textRef = useRef<Object3D>(null);

	// Animation state
	const animationStartTime = useRef<number | null>(null);
	const [middleCircleOpacity, setMiddleCircleOpacity] = useState(0);
	const [topCircleOpacity, setTopCircleOpacity] = useState(0);
	const [textOpacity, setTextOpacity] = useState(0);
	const [lineProgress, setLineProgress] = useState(0); // 0 to 1 for line animation

	// Reset animation when visibility changes
	useEffect(() => {
		if (visible) {
			animationStartTime.current = null; // Will be set on first frame
			setMiddleCircleOpacity(0);
			setTopCircleOpacity(0);
			setTextOpacity(0);
			setLineProgress(0);
		}
	}, [visible]);

	// Base positions for the middle indicator circle (starting point for lines)
	const middleCirclePosition = useMemo(() => new Vector3(
		-0.09,
		isLeft ? -0.020 : -0.027,
		isLeft ? 0.13 : 0.392
	), [isLeft]);

	// Final positions for lines
	// The top position adjusts based on archHeightValue to match the actual arch height
	// The shader applies: transformed.y += g * archHeight / 106.0, then model is scaled by 106
	// So actual displacement = g * archHeight, where g = 1.0 at the peak
	const verticalLineEndPosition = useMemo(() => new Vector3(
		middleCirclePosition.x, // Use same X as middle circle for vertical alignment
		BASE_TOP_Y + (archHeightValue * ARCH_HEIGHT_SCALE),
		middleCirclePosition.z // Use same Z as middle circle
	), [isLeft, archHeightValue, middleCirclePosition]);

	// Bottom line extends from middle circle to the label position
	const bottomLineEndPosition = useMemo(() => new Vector3(
		isLeft ? 1.93 : 1.96,
		middleCirclePosition.y, // Keep same Y as middle circle for horizontal reference
		isLeft ? 0.248 : 0.25
	), [isLeft, middleCirclePosition]);

	// Animated line points
	const animatedVerticalLinePoints = useMemo(() => {
		// Interpolate from middle circle position to final top position
		const currentEndY = MathUtils.lerp(middleCirclePosition.y, verticalLineEndPosition.y, lineProgress);
		return [
			middleCirclePosition.clone(),
			new Vector3(middleCirclePosition.x, currentEndY, middleCirclePosition.z)
		];
	}, [middleCirclePosition, verticalLineEndPosition, lineProgress]);

	// Top circle position - at the end of the vertical line (top of the arch)
	const topCirclePosition = useMemo(() =>
		verticalLineEndPosition.clone()
	, [verticalLineEndPosition]);

	const animatedBottomLinePoints = useMemo(() => {
		// Interpolate from middle circle to final end position
		const endX = MathUtils.lerp(middleCirclePosition.x, bottomLineEndPosition.x, lineProgress);
		const endY = MathUtils.lerp(middleCirclePosition.y, bottomLineEndPosition.y, lineProgress);
		const endZ = MathUtils.lerp(middleCirclePosition.z, bottomLineEndPosition.z, lineProgress);

		return [
			middleCirclePosition.clone(), // Start at middle circle
			new Vector3(endX, endY, endZ) // Animate to end position
		];
	}, [middleCirclePosition, bottomLineEndPosition, lineProgress]);

	// Text position (at the end of angled line)
	const textPosition = useMemo(() => new Vector3(
		bottomLineEndPosition.x,
		bottomLineEndPosition.y - 0.04,
		bottomLineEndPosition.z + (isLeft ? 0.06 : -0.05)
	), [bottomLineEndPosition, isLeft]);

	// Animation frame
	useFrame((state) => {
		if (!visible) return;

		// Initialize animation start time
		if (animationStartTime.current === null) {
			animationStartTime.current = state.clock.elapsedTime;
		}

		const elapsed = state.clock.elapsedTime - animationStartTime.current;

		// 1. Middle circle appears first (after startDelay)
		if (elapsed >= startDelay) {
			const middleProgress = Math.min((elapsed - startDelay) / MIDDLE_CIRCLE_FADE_DURATION, 1);
			setMiddleCircleOpacity(middleProgress);
		}

		// 2. Lines animate from middle circle position
		const linesStartTime = startDelay + LINES_START_DELAY;
		if (elapsed >= linesStartTime) {
			const linesProgress = Math.min((elapsed - linesStartTime) / LINES_ANIMATION_DURATION, 1);
			// Use easeOutCubic for smooth animation
			const easedProgress = 1 - Math.pow(1 - linesProgress, 3);
			setLineProgress(easedProgress);
		}

		// 3. Top circle appears when vertical line finishes
		const topCircleStartTime = startDelay + TOP_CIRCLE_DELAY;
		if (elapsed >= topCircleStartTime) {
			const topProgress = Math.min((elapsed - topCircleStartTime) / 0.2, 1);
			setTopCircleOpacity(topProgress);
		}

		// 4. Text fades in when bottom line finishes
		const textStartTime = startDelay + TEXT_FADE_DELAY;
		if (elapsed >= textStartTime) {
			const textProgress = Math.min((elapsed - textStartTime) / TEXT_FADE_DURATION, 1);
			setTextOpacity(textProgress);

			// Update text opacity directly on the ref
			if (textRef.current) {
				// @ts-ignore
				textRef.current.fillOpacity = textProgress;
			}
		}

		// Self-sustain frames until the full animation sequence is complete
		if (textOpacity < 1) {
			invalidate();
		}
	});

	if (!visible) return null;

	return (
		<group>
			<group>
				{/* Top indicator circle - appears after vertical line animation */}
				{/* Position adjusts based on arch height to touch the highest point */}
				<Circle
					args={[0.014, 64]} // radius, segments
					position={topCirclePosition}
					rotation={[0, Math.PI / 2, 0]} // orient to XZ plane
				>
					<meshBasicMaterial
						color="black"
						transparent
						opacity={topCircleOpacity}
					/>
				</Circle>

				{/* Vertical height indicator line - animates from middle circle */}
				{lineProgress > 0 && (
					<Line
						points={animatedVerticalLinePoints}
						color={'black'}
						dashed={false}
					/>
				)}

				{/* Middle indicator circle - appears first */}
				<Circle
					args={[0.014, 64]} // radius, segments
					position={[middleCirclePosition.x, middleCirclePosition.y, middleCirclePosition.z]}
					rotation={[0, Math.PI / 2, 0]} // orient to XZ plane
				>
					<meshBasicMaterial
						color="black"
						transparent
						opacity={middleCircleOpacity}
					/>
				</Circle>

				{/* Bottom indicator line (horizontal reference) - animates from middle circle */}
				{lineProgress > 0 && (
					<Line
						points={animatedBottomLinePoints}
						color={'black'}
						dashed={false}
					/>
				)}
			</group>

			{/* Text label - fades in after bottom line animation */}
			<Text
				anchorX={isLeft ? 'left' : 'right'}
				rotation={[0, Math.PI / 2, 0]}
				position={textPosition}
				ref={textRef}
				fontSize={0.05}
				fontWeight="bold"
				color='black' /* Match the color from ME3DScene */
				fillOpacity={textOpacity}
			>
				{archHeight.toUpperCase()}
			</Text>
		</group>
	);
};
